import { useMemo, useEffect } from "react";
import { formatCurrencyForTable, formatPercentageForTable } from "../functions/quoteFunctions";
import { itemStats } from "../functions/quoteOverviewCalculations";
import DataTableMatrix from "../../../components/DataTableMatrix";

const QuoteOverviewGeneralContractTable = ({quoteItems, setExportData}) => {

    const allQuoteItemStats = useMemo(() => itemStats(quoteItems), [quoteItems]);
    const year1QuoteItemStats = useMemo(() => itemStats(quoteItems, 1), [quoteItems]);

    const rows = [
        {
            key: "revenue",
            label: "Revenue",
            dataType: "currency"
        },
        {
            key: "thirdPartyCosts",
            label: "Third Party Costs",
            dataType: "currency"
        },
        {
            key: "rlecAmount",
            label: "RLEC £",
            dataType: "currency"
        },
        {
            key: "rlecPercent",
            label: "RLEC %",
            dataType: "percentage"
        },
        {
            key: "internalCosts",
            label: "Internal Costs",
            dataType: "currency"
        },
        {
            key: "grossMargin",
            label: "Gross Margin",
            dataType: "currency"
        },
        {
            key: "gmPercent",
            label: "GM %",
            dataType: "percentage"
        }
    ];

    const cols = [
        {
            key: "install",
            label: "Install"
        },
        {
            key: "annual",
            label: "Annual"
        },
        {
            key: "year1",
            label: "Year 1"
        },
        {
            key: "contract",
            label: "Contract"
        }
    ];


const matrixData = useMemo(() => ({
    revenue: {
        install: {
        value: "totalUpfrontPrice",
        render: function () { return formatCurrencyForTable(allQuoteItemStats[this.value]); }
        },
        annual: {
        value: "totalAnnualPrice",
        render: function () { return formatCurrencyForTable(allQuoteItemStats[this.value]); }
        },
        year1: {
        value: "totalUpfrontAndRecurringPrice",
        render: function () { return formatCurrencyForTable(year1QuoteItemStats[this.value]); }
        },
        contract: {
        value: "totalContractPrice",
        render: function () { return formatCurrencyForTable(allQuoteItemStats[this.value]); }
        }
    },
    thirdPartyCosts: {
        install: {
        value: "totalExternalUpfrontCost",
        render: function () { return formatCurrencyForTable(allQuoteItemStats[this.value]); }
        },
        annual: {
        value: "totalExternalAnnualCost",
        render: function () { return formatCurrencyForTable(allQuoteItemStats[this.value]); }
        },
        year1: {
        value: "totalExternalUpfrontAndRecurringCost",
        render: function () { return formatCurrencyForTable(year1QuoteItemStats[this.value]); }
        },
        contract: {
        value: "totalExternalContractCost",
        render: function () { return formatCurrencyForTable(allQuoteItemStats[this.value]); }
        }
    },
    rlecAmount: {
        install: {
        value: "totalUpfrontRLEC",
        render: function () { return formatCurrencyForTable(allQuoteItemStats[this.value]); }
        },
        annual: {
        value: "totalAnnualRLEC",
        render: function () { return formatCurrencyForTable(allQuoteItemStats[this.value]); }
        },
        year1: {
        value: "totalUpfrontAndRecurringRLEC",
        render: function () { return formatCurrencyForTable(year1QuoteItemStats[this.value]); }
        },
        contract: {
        value: "totalContractRLEC",
        render: function () { return formatCurrencyForTable(allQuoteItemStats[this.value]); }
        }
    },
    rlecPercent: {
        install: {
        value: "totalUpfrontRLECPercentage",
        render: function () { return formatPercentageForTable(allQuoteItemStats[this.value]); }
        },
        annual: {
        value: "totalAnnualRLECPercentage",
        render: function () { return formatPercentageForTable(allQuoteItemStats[this.value]); }
        },
        year1: {
        value: "totalUpfrontAndRecurringRLECPercentage",
        render: function () { return formatPercentageForTable(year1QuoteItemStats[this.value]); }
        },
        contract: {
        value: "totalContractRLECPercentage",
        render: function () { return formatPercentageForTable(allQuoteItemStats[this.value]); }
        }
    },
    internalCosts: {
        install: {
        value: "totalInternalUpfrontCost",
        render: function () { return formatCurrencyForTable(allQuoteItemStats[this.value]); }
        },
        annual: {
        value: "totalInternalAnnualCost",
        render: function () { return formatCurrencyForTable(allQuoteItemStats[this.value]); }
        },
        year1: {
        value: "totalInternalUpfrontAndRecurringCost",
        render: function () { return formatCurrencyForTable(year1QuoteItemStats[this.value]); }
        },
        contract: {
        value: "totalInternalContractCost",
        render: function () { return formatCurrencyForTable(allQuoteItemStats[this.value]); }
        }
    },
    grossMargin: {
        install: {
        value: "totalUpfrontMargin",
        render: function () { return formatCurrencyForTable(allQuoteItemStats[this.value]); }
        },
        annual: {
        value: "totalAnnualMargin",
        render: function () { return formatCurrencyForTable(allQuoteItemStats[this.value]); }
        },
        year1: {
        value: "totalUpfrontAndRecurringMargin",
        render: function () { return formatCurrencyForTable(year1QuoteItemStats[this.value]); }
        },
        contract: {
        value: "totalContractMargin",
        render: function () { return formatCurrencyForTable(allQuoteItemStats[this.value]); }
        }
    },
    gmPercent: {
        install: {
        value: "totalUpfrontMarginPercentage",
        render: function () { return formatPercentageForTable(allQuoteItemStats[this.value]); }
        },
        annual: {
        value: "totalAnnualMarginPercentage",
        render: function () { return formatPercentageForTable(allQuoteItemStats[this.value]); }
        },
        year1: {
        value: "totalUpfrontAndRecurringMarginPercentage",
        render: function () { return formatPercentageForTable(year1QuoteItemStats[this.value]); }
        },
        contract: {
        value: "totalContractMarginPercentage",
        render: function () { return formatPercentageForTable(allQuoteItemStats[this.value]); }
        }
    }}), [allQuoteItemStats, year1QuoteItemStats]);

    const convertDataTableToExportFormat = (matrixData) => {
        const rowsForExport = [];

        rowsForExport.push([{value: "", type:"text", style: "header", borderBottom: true, borderRight: true}, ...cols.map(col => ({ value: col.label, type: "text", style: "header", borderBottom: true, align: "right" }))]);

        for (const row of rows) {
                rowsForExport.push(
                    [{value: row.label, type:"text", style: "header", borderRight: true}, ...(cols.map(column => ({
                        value: matrixData[row.key][column.key].render ? matrixData[row.key][column.key].render() : matrixData[row.key][column.key].value,
                    dataType: row.dataType || "text"
                })))]
            );
        }

        return rowsForExport;
    };
    
    useEffect(() => {   
        setExportData(prev => {
            if (!matrixData) return prev;

            const otherSheets = prev?.sheets?.filter(sheet => sheet.id !== "general") || [];
            const newGeneralTabData = {
                id: "general",
                name: "General",
                sortOrder: 1,
                tables: (prev?.sheets?.find(sheet => sheet.id === "general")?.tables || []).filter(t => t.header !== "Contract Overview")
            }

            newGeneralTabData.tables.push({
                header: "Contract Overview",
                sortOrder: 1,
                rows: convertDataTableToExportFormat(matrixData),
            });

            return (
            {
                ...prev,
                sheets: [
                    ...otherSheets,
                    newGeneralTabData
                ]
            })

        });
    }, [matrixData]);


    return (
        <>
            <DataTableMatrix
                name="contract-overview"
                label="Contract Overview"
                columns={cols}
                rows={rows}
                data={matrixData}
                width="auto"
            />
        </>
    );
}

export default QuoteOverviewGeneralContractTable;